import { IconCheckDefault } from '@iwoca/icons';
import { clsx } from 'clsx';

import { CopyEmailButton } from './CopyEmailButton';
import { getStageGroups, StageGroupKey, StageGroupProgress } from './progress';
import { TappableTooltip } from '../../../../../components/TappableTooltip/TappableTooltip';
import { CustomerRow } from '../../../CustomersV2.types';

const StageBars = ({ group }: { group: StageGroupProgress }) => (
  <div className="flex gap-s">
    {Array.from({ length: group.required }).map((_, i) => (
      <div
        key={i}
        className={clsx(
          'h-xs flex-1 rounded-s',
          i < group.completed
            ? group.isComplete
              ? 'bg-secondary-40'
              : 'bg-primary-10'
            : 'bg-secondary-90',
        )}
      />
    ))}
  </div>
);

const StageLabel = ({ group }: { group: StageGroupProgress }) => (
  <div
    className={clsx(
      'flex items-center gap-2xs text-xs font-med',
      group.isComplete || group.completed === 0
        ? 'text-secondary-40'
        : 'text-primary-10',
    )}
  >
    {group.isComplete && (
      <IconCheckDefault data-testid="stage-group-tick" height="16" width="16" />
    )}
    {group.label}
  </div>
);

const currentGroupKey = (row: CustomerRow): StageGroupKey =>
  row.application_stage.value === 'security' ? 'security' : 'business_details';

export const OnboardingProgressCell = ({
  row,
  /** Mobile cards stack the count above the bars and drop the tooltips. */
  stacked = false,
  /**
   * Rendered beside the step count, in the stacked layout only. Always drawn
   * even when the current group has no count to show, so the explainer it opens
   * stays reachable on a row whose steps are all ticked off.
   */
  infoTrigger,
}: {
  row: CustomerRow;
  stacked?: boolean;
  infoTrigger?: React.ReactNode;
}) => {
  const groups = getStageGroups(row);
  const countFor = (group: StageGroupProgress) => {
    // A finished group is already marked with a tick, and a group with no
    // required steps has nothing to count.
    if (group.required === 0 || group.isComplete) return null;
    return `${group.completed}/${group.required} steps completed`;
  };

  if (stacked) {
    const current = currentGroupKey(row);
    const currentCount = countFor(
      groups.find((group) => group.key === current) ?? groups[0],
    );

    // The count spans the card rather than sitting in its group's column, so the
    // trigger keeps one position down a list of rows at differing stages.
    return (
      <div className="flex w-full flex-col gap-s">
        {(currentCount || infoTrigger) && (
          <div className="flex items-center gap-s text-xs font-reg text-primary-10">
            {currentCount}
            {infoTrigger}
          </div>
        )}
        {/* Two shared row tracks so both columns' bars stay on one line. */}
        <div className="grid w-full grid-cols-2 grid-rows-[auto_auto] gap-x-l">
          {groups.map((group) => (
            <div
              key={group.key}
              className="row-span-2 grid grid-rows-subgrid gap-s"
            >
              <StageBars group={group} />
              <StageLabel group={group} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center gap-l">
      <div className="flex flex-1 items-start gap-l">
        {groups.map((group) => {
          const count = countFor(group);

          return (
            <div
              key={group.key}
              // Orion's Tooltip wraps its child in a `display:flex;
              // width:max-content` span, which would collapse the flex-1 bars to
              // zero width — invisible, and with no area left to tap. Stretch
              // the wrapper to the column and let the bar row fill it. Matched by
              // descendant because a coarse pointer nests a second wrapper span
              // for the tap target.
              className="flex flex-1 flex-col gap-s [&_span>*]:grow [&_span]:w-full"
            >
              {count ? (
                // `fixed` does double duty: Orion's table is `overflow: hidden`,
                // which would clip an absolutely-positioned bubble at the table
                // edge, and it also makes the viewport (not the table) the flip
                // boundary. Without it the top rows had no room inside the table,
                // so floating-ui flipped the bubble downward while Orion left
                // `data-popper-placement` at `top` — leaving the arrow inverted.
                <TappableTooltip
                  className="text-primary-10"
                  content={count}
                  placement="top"
                  strategy="fixed"
                >
                  <StageBars group={group} />
                </TappableTooltip>
              ) : (
                <StageBars group={group} />
              )}
              <StageLabel group={group} />
            </div>
          );
        })}
      </div>
      <CopyEmailButton email={row.email} />
    </div>
  );
};
